import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { AxiosError } from "axios";
import Icon from "@mdi/react";
import { mdiDomain, mdiChevronLeft } from "@mdi/js";

import { useToast } from "../../context/ToastContext";
import { getEmpresasEmpresa, getGetEmpresasEmpresaQueryKey, postEmpresasCadastrar, postEmpresasAtualizar } from "../../config/swagger";
import type { CadastrarEmpresaRequest } from "../../model/cadastrarEmpresaRequest";
import type { AtualizarEmpresaRequest } from "../../model/atualizarEmpresaRequest";
import type { CadastrarEmpresaResponseIResponse } from "../../model/cadastrarEmpresaResponseIResponse";
import type { AtualizarEmpresaResponseIResponse } from "../../model/atualizarEmpresaResponseIResponse";
import type { ExibirEmpresaResponse } from "../../model/exibirEmpresaResponse";

import Layout from "../../components/Layout";

const toastTitle = "Empresa";

export default function EmpresaFormulario() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { handleToast } = useToast();

	const [formStatus, setFormStatus] = useState(id ? false : true);
	const [formSaving, setFormSaving] = useState(false);

	const { data, isLoading, isFetching, isRefetching } = useQuery(getGetEmpresasEmpresaQueryKey({ id: Number(id) }), () => fetchData(id!), { enabled: !!id && !formStatus });

	const { register, handleSubmit, reset } = useForm<ExibirEmpresaResponse>({ defaultValues: data?.Data });

	const mutationPost = useMutation(mutatePost, { onSuccess: mutatePostSuccess, onError: mutateError });
	const mutationPut = useMutation(mutatePut, { onSuccess: mutatePutSuccess, onError: mutateError });

	async function fetchData(id: string) {
		const resp = await getEmpresasEmpresa({ id: Number(id) });
		reset(resp.Data);
		return resp;
	}

	async function mutatePost(data: CadastrarEmpresaRequest) {
		setFormSaving(true);
		return await postEmpresasCadastrar(data);
	}

	function mutatePostSuccess(resp: CadastrarEmpresaResponseIResponse) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		if (resp.Data?.id) {
			navigate("/empresaFormulario/" + resp.Data.id);
		}
		setFormStatus(false);
		setFormSaving(false);
	}

	async function mutatePut(data: AtualizarEmpresaRequest) {
		setFormSaving(true);
		return await postEmpresasAtualizar(data);
	}

	function mutatePutSuccess(resp: AtualizarEmpresaResponseIResponse) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		setFormStatus(false);
		setFormSaving(false);
	}

	function mutateError(resp: AxiosError) {
		handleToast(toastTitle, `Problema na operação: ${resp.message}`, 5000, "danger");
		setFormSaving(false);
	}

	return (
		<Layout>
			<Form
				onSubmit={handleSubmit((values) => {
					if (id) {
						mutationPut.mutate({
							id: Number(id),
							razaoSocial: values.razaoSocial,
							nomeFantasia: values.nomeFantasia,
							cnpj: values.cnpj,
							endereco: values.endereco,
							bairro: values.bairro,
							telefone: values.telefone,
							site: values.site,
							email: values.email,
							cep: values.cep,
						});
					} else {
						mutationPost.mutate({
							razaoSocial: values.razaoSocial,
							nomeFantasia: values.nomeFantasia,
							cnpj: values.cnpj,
							endereco: values.endereco,
							bairro: values.bairro,
							telefone: values.telefone,
							site: values.site,
							email: values.email,
							cep: values.cep,
						});
					}
				})}
			>
				<h5 className="mt-4 mb-4 d-flex align-items-center fw-light">
					<Link to={"/empresas"} className="d-flex align-items-center text-decoration-none">
						<Icon path={mdiChevronLeft} size={1} className="me-1" /> <Icon path={mdiDomain} size={1} className="me-2" /> Formulário de Empresa
					</Link>
					{(isLoading || isFetching || isRefetching) && <Spinner size="sm" className="ms-1" variant="secondary" />}
					<div className="d-flex gap-2 float-right ms-auto" style={{ marginTop: -10, marginBottom: -10 }}>
						{!formStatus && (
							<Button
								variant="dark"
								className="rounded-3 shadow-sm"
								onClick={() => {
									setFormStatus(true);
								}}
							>
								Editar Informações
							</Button>
						)}
						{formStatus && (
							<>
								<Button
									variant="light"
									className="rounded-3 shadow-sm"
									type="button"
									onClick={() => {
										if (id) {
											setFormStatus(false);
										} else {
											navigate("/empresas");
										}
									}}
									disabled={formSaving}
								>
									Cancelar
								</Button>
								<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
									{formSaving ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" /> Salvando
										</>
									) : (
										"Salvar Informações"
									)}
								</Button>
							</>
						)}
					</div>
				</h5>

				<Card className="mb-3">
					<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">Informações Básicas</Card.Header>
					<Card.Body>
						<Form.Group className="mb-3" controlId="razaoSocial">
							<Form.Label>Razão Social</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("razaoSocial", { required: true })} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="nomeFantasia">
							<Form.Label>Nome Fantasia</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("nomeFantasia")} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="cnpj">
							<Form.Label>CNPJ</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("cnpj")} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="endereco">
							<Form.Label>Endereço</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("endereco")} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="bairro">
							<Form.Label>Bairro</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("bairro")} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="cep">
							<Form.Label>CEP</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("cep")} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="telefone">
							<Form.Label>Telefone</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("telefone")} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="email">
							<Form.Label>E-mail</Form.Label>
							<Form.Control type="email" placeholder="Informe aqui" {...register("email")} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="site">
							<Form.Label>Site</Form.Label>
							<Form.Control type="url" placeholder="Informe aqui" {...register("site")} disabled={!formStatus} />
						</Form.Group>
					</Card.Body>
				</Card>

				<div className="d-flex gap-2 mb-4">
					{!formStatus && (
						<Button
							variant="dark"
							className="rounded-3 shadow-sm"
							onClick={() => {
								setFormStatus(true);
							}}
						>
							Editar Informações
						</Button>
					)}
					{formStatus && (
						<>
							<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
								{formSaving ? (
									<>
										<Spinner animation="border" size="sm" className="me-2" /> Salvando
									</>
								) : (
									"Salvar Informações"
								)}
							</Button>
							<Button
								variant="light"
								className="rounded-3 shadow-sm"
								type="button"
								onClick={() => {
									if (id) {
										setFormStatus(false);
									} else {
										navigate("/empresas");
									}
								}}
								disabled={formSaving}
							>
								Cancelar
							</Button>
						</>
					)}
				</div>
			</Form>
		</Layout>
	);
}
